import React, { useRef, useState, useCallback, useEffect, useMemo } from "react";
import { useResourcesPlanner } from "../ResourcesPlannerContext";
import {
  generateDays,
  groupDaysByMonth,
  groupDaysByWeek,
  isWeekend,
  isToday,
  DAY_WIDTHS,
  getDatePosition,
  getDateSpanWidth,
  positionToDate,
} from "../utils/dateUtils";
import AllocationBar from "./AllocationBar";
import styles from "../ResourcesPlanner.module.scss";
import moment from "moment";

const ALLOCATION_BAR_HEIGHT = 20;
const ALLOCATION_BAR_GAP = 5;
const ROW_MIN_HEIGHT = 40;
const RESOURCE_ROW_MIN_HEIGHT = 52;

/**
 * Build a flat list of visible rows based on expand state.
 * Each row has: { type, id, resource, project?, scope?, task?, allocations[] }
 */
function buildVisibleRows(resources, isExpanded) {
  const rows = [];

  for (const resource of resources) {
    const resourceExp = isExpanded(resource.id);

    // Collect ALL allocations for this resource
    const allAllocations = [];
    for (const project of resource.projects || []) {
      for (const scope of project.scopes || []) {
        for (const task of scope.tasks || []) {
          if (task.allocation?.startDate && task.allocation?.endDate) {
            allAllocations.push({ task, scope, project, allocation: task.allocation });
          }
        }
      }
    }

    if (!resourceExp) {
      // Collapsed: all allocations stacked in one resource row
      rows.push({
        type: "resource",
        id: `r-${resource.id}`,
        resource,
        allocations: allAllocations,
      });
    } else {
      // Resource row (no allocations - they move to children)
      rows.push({
        type: "resource",
        id: `r-${resource.id}`,
        resource,
        allocations: [],
      });

      for (const project of resource.projects || []) {
        const projectExp = isExpanded(resource.id, project.id);

        // Collect project-level allocations
        const projectAllocations = [];
        for (const scope of project.scopes || []) {
          for (const task of scope.tasks || []) {
            if (task.allocation?.startDate && task.allocation?.endDate) {
              projectAllocations.push({ task, scope, project, allocation: task.allocation });
            }
          }
        }

        if (!projectExp) {
          // Project collapsed: show project's allocations
          rows.push({
            type: "project",
            id: `r-${resource.id}-p-${project.id}`,
            resource,
            project,
            allocations: projectAllocations,
          });
        } else {
          // Project row (no allocations - they move to scopes)
          rows.push({
            type: "project",
            id: `r-${resource.id}-p-${project.id}`,
            resource,
            project,
            allocations: [],
          });

          for (const scope of project.scopes || []) {
            const scopeExp = isExpanded(resource.id, project.id, scope.id);

            const scopeAllocations = [];
            for (const task of scope.tasks || []) {
              if (task.allocation?.startDate && task.allocation?.endDate) {
                scopeAllocations.push({ task, scope, project, allocation: task.allocation });
              }
            }
            for (const group of scope.taskGroups || []) {
              for (const task of group.tasks || []) {
                if (task.allocation?.startDate && task.allocation?.endDate) {
                  scopeAllocations.push({ task, scope, project, allocation: task.allocation });
                }
              }
            }

            if (!scopeExp) {
              // Scope collapsed: show scope's allocations
              rows.push({
                type: "scope",
                id: `r-${resource.id}-p-${project.id}-s-${scope.id}`,
                resource,
                project,
                scope,
                allocations: scopeAllocations,
              });
            } else {
              // Scope row (no allocations)
              rows.push({
                type: "scope",
                id: `r-${resource.id}-p-${project.id}-s-${scope.id}`,
                resource,
                project,
                scope,
                allocations: [],
              });

              // Task Group rows
              for (const group of scope.taskGroups || []) {
                const groupExp = isExpanded(resource.id, project.id, scope.id, group.id);

                const groupAllocations = [];
                for (const task of group.tasks || []) {
                  if (task.allocation?.startDate && task.allocation?.endDate) {
                    groupAllocations.push({ task, scope, project, allocation: task.allocation });
                  }
                }

                if (!groupExp) {
                  // Task group collapsed: show its allocations
                  rows.push({
                    type: "taskGroup",
                    id: `r-${resource.id}-p-${project.id}-s-${scope.id}-g-${group.id}`,
                    resource,
                    project,
                    scope,
                    group,
                    allocations: groupAllocations,
                  });
                } else {
                  // Task group row (no allocations - they move to tasks)
                  rows.push({
                    type: "taskGroup",
                    id: `r-${resource.id}-p-${project.id}-s-${scope.id}-g-${group.id}`,
                    resource,
                    project,
                    scope,
                    group,
                    allocations: [],
                  });

                  const sortedGroupTasks = [...(group.tasks || [])].sort((a, b) => {
                    const aD = a.allocation?.startDate || "9999";
                    const bD = b.allocation?.startDate || "9999";
                    return aD.localeCompare(bD);
                  });

                  for (const task of sortedGroupTasks) {
                    const taskAllocations = [];
                    if (task.allocation?.startDate && task.allocation?.endDate) {
                      taskAllocations.push({ task, scope, project, allocation: task.allocation });
                    }
                    rows.push({
                      type: "task",
                      id: `r-${resource.id}-p-${project.id}-s-${scope.id}-g-${group.id}-t-${task.id}`,
                      resource,
                      project,
                      scope,
                      group,
                      task,
                      allocations: taskAllocations,
                    });
                  }
                }
              }

              // Task rows (ungrouped)
              const sortedTasks = [...(scope.tasks || [])].sort((a, b) => {
                const aD = a.allocation?.startDate || "9999";
                const bD = b.allocation?.startDate || "9999";
                return aD.localeCompare(bD);
              });

              for (const task of sortedTasks) {
                const taskAllocations = [];
                if (task.allocation?.startDate && task.allocation?.endDate) {
                  taskAllocations.push({ task, scope, project, allocation: task.allocation });
                }
                rows.push({
                  type: "task",
                  id: `r-${resource.id}-p-${project.id}-s-${scope.id}-t-${task.id}`,
                  resource,
                  project,
                  scope,
                  task,
                  allocations: taskAllocations,
                });
              }
            }
          }
        }
      }
    }
  }

  return rows;
}

/**
 * Compute lane assignments and row height for a set of allocations.
 */
function computeLanes(allocations) {
  if (allocations.length === 0) return { laneAssignments: [], laneCount: 0 };

  const sorted = [...allocations].sort((a, b) =>
    a.allocation.startDate.localeCompare(b.allocation.startDate)
  );

  const lanes = [];
  const laneAssignments = [];

  for (const alloc of sorted) {
    const allocStart = moment(alloc.allocation.startDate);
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      if (allocStart.isAfter(moment(lanes[i]), "day")) {
        lanes[i] = alloc.allocation.endDate;
        laneAssignments.push({ ...alloc, lane: i });
        placed = true;
        break;
      }
    }
    if (!placed) {
      lanes.push(alloc.allocation.endDate);
      laneAssignments.push({ ...alloc, lane: lanes.length - 1 });
    }
  }

  return { laneAssignments, laneCount: lanes.length };
}

export default function PlannerTimeline({ timelineRef, scrollWrapperRef }) {
  const {
    viewType,
    dateRange,
    resources,
    isLoadingMore,
    hasMore,
    loadMore,
    openCreateTask,
    registerScrollToDate,
    isExpanded,
    selectedRange,
    summaryPeriod,
  } = useResourcesPlanner();

  const dayWidth = DAY_WIDTHS[viewType];

  const [selection, setSelection] = useState(null);
  const selectionStartRef = useRef(null);

  const days = useMemo(
    () => generateDays(dateRange.startDate, dateRange.endDate),
    [dateRange]
  );
  const monthGroups = useMemo(() => groupDaysByMonth(days), [days]);
  const weekGroups = useMemo(() => groupDaysByWeek(days), [days]);
  const totalWidth = days.length * dayWidth;

  // ── Infinite scroll ─────────────────────────────────────────────────────
  const handleScroll = useCallback(() => {
    const el = scrollWrapperRef?.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) {
      if (hasMore && !isLoadingMore) loadMore();
    }
  }, [hasMore, isLoadingMore, loadMore, scrollWrapperRef]);

  useEffect(() => {
    const el = scrollWrapperRef?.current;
    if (el) {
      el.addEventListener("scroll", handleScroll);
      return () => el.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll, scrollWrapperRef]);

  // ── Scroll to date ──────────────────────────────────────────────────────
  useEffect(() => {
    if (timelineRef) {
      timelineRef.current = {
        scrollToDate: (date) => {
          const pos = getDatePosition(date, dateRange.startDate, dayWidth);
          if (pos !== null && scrollWrapperRef?.current) {
            const wrapper = scrollWrapperRef.current;
            const panelWidth = wrapper.firstChild?.firstChild?.clientWidth ?? 0;
            const visibleWidth = wrapper.clientWidth - panelWidth;
            wrapper.scrollLeft = Math.max(0, pos - visibleWidth / 2 + dayWidth / 2);
          }
        },
      };
    }
  }, [timelineRef, dateRange.startDate, dayWidth, scrollWrapperRef]);

  // ── Register scroll-to-date ─────────────────────────────────────────────
  useEffect(() => {
    registerScrollToDate((dateStr) => {
      const pos = getDatePosition(dateStr, dateRange.startDate, dayWidth);
      if (pos !== null && scrollWrapperRef?.current) {
        const wrapper = scrollWrapperRef.current;
        const panelWidth = wrapper.firstChild?.firstChild?.clientWidth ?? 0;
        const visibleWidth = wrapper.clientWidth - panelWidth;
        wrapper.scrollLeft = Math.max(0, pos - visibleWidth / 2 + dayWidth / 2);
      }
    });
  }, [registerScrollToDate, dateRange.startDate, dayWidth, scrollWrapperRef]);

  // ── Drag to select ──────────────────────────────────────────────────────
  const getOffsetX = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return e.clientX - rect.left;
  }, []);

  const handleTimelineMouseDown = useCallback(
    (e, resource, project = null, scope = null, group = null, rowId = null) => {
      const offsetX = getOffsetX(e);
      const clickedDate = positionToDate(offsetX, dateRange.startDate, dayWidth);
      if (isWeekend(moment(clickedDate))) return;
      selectionStartRef.current = { resource, startDate: clickedDate, project, scope, group, rowId };
      setSelection({ resource, startDate: clickedDate, endDate: clickedDate, project, scope, group, rowId });
    },
    [dateRange.startDate, dayWidth, getOffsetX]
  );

  const handleTimelineMouseMove = useCallback(
    (e) => {
      if (!selectionStartRef.current) return;
      const offsetX = getOffsetX(e);
      const endDate = positionToDate(offsetX, dateRange.startDate, dayWidth);
      if (isWeekend(moment(endDate))) return;
      const { resource, startDate, project, scope, group, rowId } = selectionStartRef.current;
      const sortedStart = startDate < endDate ? startDate : endDate;
      const sortedEnd = startDate < endDate ? endDate : startDate;
      setSelection({ resource, startDate: sortedStart, endDate: sortedEnd, project, scope, group, rowId });
    },
    [dateRange.startDate, dayWidth, getOffsetX]
  );

  const handleTimelineMouseUp = useCallback(() => {
    if (selectionStartRef.current && selection) {
      openCreateTask(selection.resource, selection.startDate, selection.endDate, selection.project || null, selection.scope || null, selection.group || null);
    }
    selectionStartRef.current = null;
    setSelection(null);
  }, [selection, openCreateTask]);

  // ── Build visible rows ──────────────────────────────────────────────────
  const visibleRows = useMemo(
    () => buildVisibleRows(resources, isExpanded),
    [resources, isExpanded]
  );

  // ── Compute row heights ─────────────────────────────────────────────────
  const rowData = useMemo(() => {
    return visibleRows.map((row) => {
      const { laneAssignments, laneCount } = computeLanes(row.allocations);
      const minH = row.type === "resource" ? RESOURCE_ROW_MIN_HEIGHT : ROW_MIN_HEIGHT;
      const contentHeight = laneCount > 0
        ? laneCount * (ALLOCATION_BAR_HEIGHT + ALLOCATION_BAR_GAP) + 8
        : 0;
      const rowHeight = Math.max(contentHeight, minH);
      return { ...row, laneAssignments, rowHeight };
    });
  }, [visibleRows]);

  // ── Today marker ────────────────────────────────────────────────────────
  const todayPos = useMemo(() => {
    return getDatePosition(moment().format("YYYY-MM-DD"), dateRange.startDate, dayWidth);
  }, [dateRange.startDate, dayWidth]);
  // ── Selected range layout ───────────────────────────────────────────────
  const selectedRangeLayout = useMemo(() => {
    if (summaryPeriod !== "custom") return null;
    if (!selectedRange?.startDate || !selectedRange?.endDate) return null;
    const clampedStart = moment.max(moment(selectedRange.startDate), moment(dateRange.startDate)).format("YYYY-MM-DD");
    const clampedEnd = moment.min(moment(selectedRange.endDate), moment(dateRange.endDate)).format("YYYY-MM-DD");
    if (moment(clampedStart).isAfter(moment(clampedEnd))) return null;
    const left = getDatePosition(clampedStart, dateRange.startDate, dayWidth);
    if (left === null) return null;
    const width = getDateSpanWidth(clampedStart, clampedEnd, dayWidth);
    return { left, width };
  }, [summaryPeriod, selectedRange, dateRange, dayWidth]);
  // ── Day cell class builder ──────────────────────────────────────────────
  const getDayCellClass = (day) => {
    let cls = styles.dayCell;
    if (viewType === "day") cls += ` ${styles.dayCellDayView}`;
    if (viewType === "month") cls += ` ${styles.dayCellCompact}`;
    if (summaryPeriod === "custom" && selectedRange && day.isBetween(moment(selectedRange.startDate), moment(selectedRange.endDate), "day", "[]")) {
      cls += ` ${styles.dayCellSelected}`;
    }
    if (isWeekend(day)) cls += ` ${styles.dayCellWeekend}`;
    if (isToday(day)) cls += ` ${styles.dayCellToday}`;
    return cls;
  };

  // ── Render header ───────────────────────────────────────────────────────
  const renderHeaders = () => {
    if (viewType === "day") {
      return (
        <div className={styles.headerRowBottom}>
          {days.map((day) => (
            <div key={day.format("YYYY-MM-DD")} className={getDayCellClass(day)} style={{ width: dayWidth, height: "67.5px" }}>
              <span className={styles.dayCellName}>{day.format("ddd")}</span>
              <span className={styles.dayCellNumber}>{day.format("D")}</span>
            </div>
          ))}
        </div>
      );
    }
    if (viewType === "week") {
      return (
        <>
          <div className={styles.headerRowTop}>
            {weekGroups.map((week) => (
              <div key={week.key} className={styles.weekCell} style={{ width: week.days.length * dayWidth }}>
                {week.label}
              </div>
            ))}
          </div>
          <div className={styles.headerRowBottom}>
            {days.map((day) => (
              <div key={day.format("YYYY-MM-DD")} className={getDayCellClass(day)} style={{ width: dayWidth }}>
                <span className={styles.dayCellName}>{day.format("dd")}</span>
                <span className={styles.dayCellNumber}>{day.format("D")}</span>
              </div>
            ))}
          </div>
        </>
      );
    }
    return (
      <>
        <div className={styles.headerRowTop}>
          {monthGroups.map((group) => (
            <div key={group.key} className={styles.monthCell} style={{ width: group.days.length * dayWidth }}>
              {group.label}
            </div>
          ))}
        </div>
        <div className={styles.headerRowBottom}>
          {days.map((day) => (
            <div key={day.format("YYYY-MM-DD")} className={getDayCellClass(day)} style={{ width: dayWidth, minHeight: dayWidth }}>
              <span className={styles.dayCellNumber}>{day.format("D")}</span>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className={styles.timelineArea} style={{ minWidth: totalWidth }}>
      <div className={styles.timelineHeaders} style={{ width: totalWidth }}>
        {renderHeaders()}
        {selectedRangeLayout && (
          <div
            className={styles.selectedRangeBar}
            style={{ left: `${selectedRangeLayout.left}px`, width: `${selectedRangeLayout.width}px` }}
          />
        )}
      </div>

      <div
        className={styles.timelineBody}
        style={{ width: totalWidth, minHeight: "100%" }}
        onMouseMove={handleTimelineMouseMove}
        onMouseUp={handleTimelineMouseUp}
        onMouseLeave={() => { selectionStartRef.current = null; setSelection(null); }}
      >
        {todayPos !== null && (
          <div className={styles.todayLine} style={{ left: `${todayPos + dayWidth / 2}px` }}>
            {/* <div className={styles.todayDiamond} /> */}
          </div>
        )}

        {selectedRangeLayout && (
          <div
            className={styles.selectedRangeColumn}
            style={{
              left: `${selectedRangeLayout.left}px`,
              width: `${selectedRangeLayout.width}px`,
              height: "100%",
            }}
          />
        )}

        {days.map((day) =>
          isWeekend(day) ? (
            <div
              key={`we-${day.format("YYYY-MM-DD")}`}
              className={styles.weekendColumn}
              style={{
                left: `${getDatePosition(day.format("YYYY-MM-DD"), dateRange.startDate, dayWidth)}px`,
                width: `${dayWidth}px`,
                height: "100%",
              }}
            />
          ) : null
        )}

        {days.map((day, idx) =>
          idx > 0 ? (
            <div
              key={`dg-${day.format("YYYY-MM-DD")}`}
              className={styles.dayGridLine}
              style={{
                left: `${getDatePosition(day.format("YYYY-MM-DD"), dateRange.startDate, dayWidth)}px`,
                height: "100%",
              }}
            />
          ) : null
        )}

        {rowData.map(({ id, type, resource, project, scope, group, laneAssignments, rowHeight }) => {
          const canCreate = type === "project" || type === "scope" || type === "taskGroup";

          return (
            <div
              key={id}
              className={`${styles.timelineRow} ${type === "resource" ? styles.timelineRowResource : ""}`}
              style={{ height: rowHeight}}
              onMouseDown={(e) => {
                if (!canCreate) return;
                if (e.target === e.currentTarget || e.target.classList.contains(styles.timelineRowInner)) {
                  handleTimelineMouseDown(
                    e,
                    resource,
                    project ? { id: project.id, name: project.name } : null,
                    scope ? { id: scope.id, name: scope.name } : null,
                    group ? { id: group.id, name: group.name } : null,
                    id
                  );
                }
              }}
            >
              {selection && selection.rowId === id && (
                <div
                  className={styles.selectionHighlight}
                  style={{
                    left: `${getDatePosition(selection.startDate, dateRange.startDate, dayWidth)}px`,
                    width: `${(moment(selection.endDate).diff(moment(selection.startDate), "days") + 1) * dayWidth}px`,
                  }}
                />
              )}

              <div className={styles.timelineRowInner}>
                {laneAssignments.map(({ task, scope, project, lane }) => (
                  <AllocationBar
                    key={task.id}
                    task={task}
                    scope={scope}
                    project={project}
                    resourceId={resource.id}
                    timelineStartDate={dateRange.startDate}
                    style={{
                      top: `${lane * (ALLOCATION_BAR_HEIGHT + ALLOCATION_BAR_GAP) + 4}px`,
                      height: `${ALLOCATION_BAR_HEIGHT}px`,
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {isLoadingMore && (
          <>
            <div className={styles.skeletonTimelineBar} style={{ width: totalWidth }}>
              <div className={styles.skeletonBar} style={{ left: '5%', width: '25%' }} />
            </div>
            <div className={styles.skeletonTimelineBar} style={{ width: totalWidth }}>
              <div className={styles.skeletonBar} style={{ left: '15%', width: '35%' }} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
